import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';

import { AuthUtils } from '../../util/auth.utils'

import { LoginModel } from '../../model/login-model';
import { RecoverModel } from '../../../data/model/recover-model';
import { HttpInternalService } from 'src/app/data/service/http.service';
import { Title } from '@angular/platform-browser';
import { NotificationsHubService } from '../hubs/notifications-hub.service';

import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SessionTimeoutDialogComponent } from 'src/app/shared/session-timeout-dialog/session-timeout-dialog.component';
import { DashboardLayoutService } from 'src/app/modules/dashboard/services/dashboard-layout.service';
import { ManageIntercom } from '../../util/manage-intercom';
import posthog from 'posthog-js';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Injector } from '@angular/core';
import { SessionService } from '../session.service';
import { EntitlementsService } from '../entitlements.service';
import { PracticeService } from 'src/app/data/service/practice.service';

// Signed out after 30 minutes with no mouse, keyboard, scroll or touch in any
// tab: 25 minutes idle, then a five-minute "Still there?" countdown.
const IDLE_SECONDS = 25 * 60;
const WARNING_SECONDS = 5 * 60;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private authenticated: boolean;
  private _notificationsHubService: NotificationsHubService;
  private idleConfigured = false;
  private idleWarning: NgbModalRef;
  private pendingCheck: Observable<boolean>;


  constructor(
    private _httpService: HttpInternalService,
    private readonly _titleService: Title,
    // private readonly _notificationsHubService: NotificationsHubService,
    private idle: Idle,
    private modalService: NgbModal,
    private router: Router,
    private injector: Injector
  ) { }


  set accessUpUserId(upUserId: string) {
    localStorage.setItem('UpUserId', upUserId);
  }

  get accessUpUserId(): string {
    return localStorage.getItem('UpUserId');
  }
  idleState = 'Not started.';
  timedOut = false;
  signIn(loginModel: LoginModel): Observable<any> {
    // Throw error, if the user is already logged in
    if (this.authenticated) {
      return throwError({ message: 'User is already logged in.' });
    }

    return this._httpService.postRequest('token', loginModel).pipe(
      switchMap((response: any) => {

        if (response.status == 0) {
          const user = response.data?.user;

          if (user) {
            this.accessUpUserId = user.id;
            this.authenticated = true;
            this.startIdleWatch();

            // SD-1218: SessionService and EntitlementsService cache with shareReplay, and
            // signing in does not reload the page -- so without this a second sign-in in the
            // same tab would show the previous user's role and menu.
            this.refreshSessionCaches();
          }
        }

        // Return a new observable with the response
        return of(response);
      })
    );

  }

  /**
   * Signs in with a one-time code from Synced Admin (its "Edit" on a template, saved section or
   * page): the backend checks the code, sets the session cookie as the login does, and names the
   * CMS editor to open (CmsEditorHandoffController). Replaces any session already in this tab.
   */
  signInWithHandoff(code: string): Observable<any> {
    return this._httpService.postRequest('cms/editor-handoff/claim', { code }).pipe(
      switchMap((response: any) => {
        const user = response?.status == 0 ? response.data?.user : null;
        if (user) {
          this.accessUpUserId = user.id;
          this.authenticated = true;
          this.startIdleWatch();
          this.refreshSessionCaches();
        }
        return of(response);
      })
    );
  }

  signOut(): Observable<any> {
    if (!this.authenticated) {
      return of(null); // No need to call backend if not authenticated
    }
    // Call backend logout API FIRST (important)
    return this._httpService.postRequest('token/logout', {}, { withCredentials: true } // 🔥 REQUIRED for cookies
    ).pipe(
      // finalize, not tap: clear the local session even if the server call fails.
      // Otherwise `authenticated` stays set and the next sign-in is refused.
      finalize(() => {
        this.stopIdleWatch();
        this.authenticated = false;
        this.notificationsHub.stop();

        // SD-1218: drop the cached user and entitlements with the rest of the session.
        this.refreshSessionCaches();
        localStorage.removeItem('AccessToken');
        localStorage.removeItem('UpUserId');
        localStorage.removeItem('SelectedOrganization');
        localStorage.removeItem('SelectedOrganizationName');
        localStorage.removeItem('UpUserDetails');
        localStorage.removeItem('accountRemember');
        localStorage.removeItem('UserName');
        localStorage.removeItem('UserId');
        localStorage.removeItem('Id');
        localStorage.removeItem('InvoceIdsStorage');
        localStorage.removeItem('IsLogedin');
        localStorage.removeItem('IsXeroDirect');

        // SD-1209: the dashboard layout cache is keyed by user and organisation, so it cannot
        // be named in the list above -- sweep it by prefix.
        DashboardLayoutService.clearAllCaches();

        sessionStorage.clear();

        // Reset analytics
        ManageIntercom.shutdown();
        try { posthog.reset(); } catch (e) { }

        this._titleService.setTitle("Synced");
        this.router.navigate(['auth/login']);
      })
    );
  }
  /**
   * Whether the session is signed in. Calls made while one is already in flight share
   * it: the route guard asks for both canActivate and canActivateChild on every
   * navigation. Once it completes, the next call checks afresh.
   */
  check(): Observable<boolean> {
    if (!this.pendingCheck) {
      this.pendingCheck = this.requestCheck().pipe(
        finalize(() => this.pendingCheck = null),
        shareReplay(1));
    }
    return this.pendingCheck;
  }

  private requestCheck(): Observable<boolean> {
    return this._httpService.getRequest('Account/me').pipe(
      switchMap((response: any) => {
      if (response && response.status === 0 && response.data !== null) {
        this.authenticated = true;
        this.startIdleWatch();
        return of(true);
      }
      this.authenticated = false;
      this.stopIdleWatch();
      return of(false);
      }),
      catchError(() => {
      this.authenticated = false;
      this.stopIdleWatch();
      return of(false);
      })
    );
  }

  /**
   * Starts the inactivity timer unless it's already running. Called on every
   * successful auth check, so it covers page reloads and Xero sign-in too —
   * it used to start only on email sign-in, and a reload switched it off.
   */
  private startIdleWatch(): void {
    if (!this.idleConfigured) {
      // Once per app lifetime: subscribing on every sign-in stacked up handlers.
      this.idleConfigured = true;
      this.idle.setIdle(IDLE_SECONDS);
      this.idle.setTimeout(WARNING_SECONDS);
      this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

      this.idle.onIdleStart.subscribe(() => this.openIdleWarning());
      this.idle.onTimeoutWarning.subscribe((secondsLeft: number) => {
        if (this.idleWarning) {
          this.idleWarning.componentInstance.secondsLeft = secondsLeft;
        }
      });
      // Any activity, in this tab or another, ends the countdown.
      this.idle.onIdleEnd.subscribe(() => this.closeIdleWarning());
      // Subscribing is what sends the logout request. This used to call
      // signOut() bare, so the server session outlived the idle timeout.
      this.idle.onTimeout.subscribe(() => {
        this.closeIdleWarning();
        this.signOut().subscribe({ error: () => { } });
      });
    }

    if (!this.idle.isRunning()) {
      this.idle.watch();
    }
  }

  private stopIdleWatch(): void {
    this.closeIdleWarning();
    if (this.idle.isRunning()) {
      this.idle.stop();
    }
  }

  private openIdleWarning(): void {
    if (this.idleWarning) {
      return;
    }
    const warning = this.modalService.open(SessionTimeoutDialogComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      windowClass: 'synced-dialog',
      backdropClass: 'synced-dialog-backdrop'
    });
    warning.componentInstance.secondsLeft = WARNING_SECONDS;
    warning.componentInstance.totalSeconds = WARNING_SECONDS;
    this.idleWarning = warning;
    warning.result.then(
      // "Stay signed in". The click is itself activity, but restart explicitly
      // in case it didn't register as an interrupt.
      () => {
        if (this.idleWarning === warning) this.idleWarning = null;
        this.idle.watch();
      },
      // Dismissed by closeIdleWarning: nothing to do.
      () => {
        if (this.idleWarning === warning) this.idleWarning = null;
      });
  }

  private closeIdleWarning(): void {
    const warning = this.idleWarning;
    this.idleWarning = null;
    warning?.dismiss();
  }

  private get notificationsHub(): NotificationsHubService {
    if (!this._notificationsHubService) {
      this._notificationsHubService = this.injector.get(NotificationsHubService);
    }
    return this._notificationsHubService;
  }

  recoverPassword(recoverModel: RecoverModel): Observable<any> {
    return this._httpService.postRequest('Account/ForgotPassword', recoverModel).pipe(
      switchMap((response: any) => {

        // Return a new observable with the response
        return of(response);
      })
    );
  }

  resetPassword(recoverModel: RecoverModel): Observable<any> {
    return this._httpService.postRequest('Account/SyncedForgotPassword', recoverModel).pipe(
      switchMap((response: any) => {

        // Return a new observable with the response
        return of(response);
      })
    );
  }

  savePassword(model): Observable<any> {
    return this._httpService.postRequest('Account/SavePassword', model).pipe(
      switchMap((response: any) => {

        // Return a new observable with the response
        return of(response);
      })
    );
  }

  ApproveSigninWithToken(model): Observable<any> {
    return this._httpService.postRequest('Account/ApproveSigninWithToken', model).pipe(
      switchMap((response: any) => {

        // Return a new observable with the response
        return of(response);
      })
    );
  }

  /**
   * SD-1218: invalidates the cached user and module entitlements.
   *
   * Resolved through the Injector rather than constructor-injected: EntitlementsService
   * depends on OrganizationService and SessionService, both of which sit downstream of the
   * HTTP stack that AuthService is part of, and taking them in the constructor makes that
   * cycle eager. Failures are swallowed -- a stale cache is a far smaller problem than a
   * sign-in or sign-out that throws.
   */
  private refreshSessionCaches(): void {
    try {
      this.injector.get(SessionService).refresh();
      this.injector.get(EntitlementsService).refresh();
      // SD-1248: practice/me is cached the same way and belongs to the user just as much.
      this.injector.get(PracticeService).refresh();
    } catch {
      // Nothing has subscribed yet, or the services are not constructible here. Either way
      // the next subscription reads fresh.
    }
  }

}
